import type { StowedElement } from '@/api/types/stowed-element'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import type { GeometricPersistOp } from '@/lib/elements/types/geometric-persist-op'
import {
  buildStowedElement,
  buildStowedVisibleRectOptions,
} from '@/lib/elements/stowed-element-builder'
import type { DocumentProjection } from '@/lib/canvas/types/document-projection'
import { IDENTITY_PROJECTION } from '@/lib/canvas/document-projection'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'

import { buildGroupGeometricBatchBodies } from './helpers/build-group-geometric-batch-bodies'
import { isGroupShape } from '@/lib/groups/group-kind'
import { isTextUnderGroup } from '@/lib/groups/text-under-group'

/**
 * `StowedElement[]` for PUT …/elements/move|resize|rotate (or batch update): the kind-7 row,
 * every **non–kind-7** shape under that group (canvas-absolute), then every **unlocked** text box
 * nested under the group (same frame), in
 * stable list order within each section.
 *
 * Pass `geometricOperation` so rotated rows include `visible*` on move/resize as well as rotate.
 */
export function buildGroupTransformPersistBatch(
  shapesInListOrder: ShapeData[],
  group: ShapeData,
  textBoxes: TextBoxData[],
  geometricOperation?: GeometricPersistOp,
  projection: DocumentProjection = IDENTITY_PROJECTION,
  fonts?: ParagraphBlockFontLookup
): StowedElement[] {
  const shapeBodies = buildGroupGeometricBatchBodies(
    shapesInListOrder,
    group,
    geometricOperation,
    projection,
    fonts
  )

  if (!isGroupShape(group)) {
    return shapeBodies
  }

  const byId = new Map(shapesInListOrder.map((s) => [s.id, s]))
  const textBodies: StowedElement[] = []

  for (const tb of textBoxes) {
    if (!isTextUnderGroup(tb, group.id, byId)) continue
    if (tb.locked === true) continue

    textBodies.push(
      buildStowedElement(
        tb,
        buildStowedVisibleRectOptions(tb.rotation, geometricOperation, geometricOperation),
        projection,
        fonts
      )
    )
  }

  return [...shapeBodies, ...textBodies]
}
